import { createContext, ReactNode } from 'react';

// A single applied filter, surfaced as a removable chip in the layout's
// active-filters row
export interface ActiveFilterChip {
  // Stable key for the chip (usually the filter field name)
  key: string;
  // Human-readable label shown on the chip
  label: string;
  // Remove just this filter
  onRemove: () => void;
}

export interface FilterConfig {
  // Unique identifier for the filter set
  id: string;
  // Title shown in the filter panel
  title: string;
  // Content to render inside the filter
  content: ReactNode;
  // Number of active filters
  activeCount?: number;
  // Applied filters surfaced as removable chips in the layout
  activeFilters?: ActiveFilterChip[];
  // Callback when filters are applied
  onApply?: () => void;
  // Callback when filters are reset
  onReset?: () => void;
  // Clear all applied filters at once (used by the active-filters row)
  onClearAll?: () => void;
  // Whether Apply button should be disabled
  isApplyDisabled?: boolean;
  // Whether Reset button should be disabled
  isResetDisabled?: boolean;
}

export interface HeaderSearchConfig {
  // Current search value
  value?: string;
  defaultValue?: string;
  // Placeholder text
  placeholder?: string;
  // Callback when search changes
  onChange: (value: string) => void;
  // Whether search is enabled
  enabled?: boolean;
}

// Named regions of the header a page may inject its own controls into.
// 'actions' is the button row on the right, between the help link and the
// filter trigger. Add a name here to open up another region.
export type HeaderSlotName = 'actions';

export type HeaderSlots = Partial<Record<HeaderSlotName, ReactNode>>;

export interface PathConfigs {
  [path: string]: {
    filterConfig: FilterConfig | null;
    searchConfig: HeaderSearchConfig | null;
    slots: HeaderSlots;
  };
}

export interface HeaderFiltersContextValue {
  filterConfig: FilterConfig | null;
  searchConfig: HeaderSearchConfig | null;
  // Page-supplied header content for the current route, keyed by slot name
  slots: HeaderSlots;
  isFilterOpen: boolean;
  setFilterConfig: (config: FilterConfig | null) => void;
  setSearchConfig: (config: HeaderSearchConfig | null) => void;
  setHeaderSlot: (name: HeaderSlotName, node: ReactNode) => void;
  openFilters: () => void;
  closeFilters: () => void;
  toggleFilters: () => void;
}

export const HeaderFiltersContext = createContext<HeaderFiltersContextValue | null>(null);
